import React, { useState, useEffect, useMemo } from 'react';
import { authFetch } from './utils/authFetch';
import { fetchGlobalConfig } from './redux/actions/globalConfigActions';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Card,
  Title,
  Group,
  Button,
  Table,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Pagination,
  Loader,
  Center,
  Modal,
  Avatar,
  TextInput,
  Menu,
  Chip,
  Stack,
  ThemeIcon,
  Overlay,
  Transition,
  Divider,
  Stepper
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCloudUpload,
  IconSearch,
  IconUser,
  IconDatabase,
  IconCheck,
  IconClock,
  IconFileDescription,
  IconArrowRight
} from '@tabler/icons-react';
import { useAuth0 } from '@auth0/auth0-react';
import { notifications } from '@mantine/notifications';
import ImportSageMakerModels from './utils/importSageMakerModels.js';
import ImportBedrockModels from './utils/importBedrockModels.js';
import ModelCardForm from './ModelCardForm';
import DocumentUpload from './components/DocumentUpload';
import { selectModelsById, fetchAllModels, deleteModel } from './redux/slices/modelsSlice';
import { selectBuilderAssessments, selectExplorerAssessments } from './redux/slices/assessmentsSlice';

function ModelInventory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    dispatch(fetchGlobalConfig('modelFormConfig'));
    // Note: fetchAllModels() is called in App.js during initialization
    // No need to call it again here - data comes from Redux state
  }, [dispatch]);

  // Fetch model card documents for indicator display
  useEffect(() => {
    const fetchModelDocs = async () => {
      try {
        const response = await authFetch('/.netlify/functions/manageAssessmentDocuments?operation=list&documentType=model_card');
        const { documents = [] } = await response.json();
        // Group by model_uuid
        const docsByModel = documents.reduce((acc, doc) => {
          if (!acc[doc.model_uuid]) acc[doc.model_uuid] = [];
          acc[doc.model_uuid].push(doc);
          return acc;
        }, {});
        setModelDocuments(docsByModel);
      } catch (err) {
        console.error('Failed to fetch model documents:', err);
      }
    };
    fetchModelDocs();
  }, []);

  // Use RTK selector only - no legacy fallback
  const modelsById = useSelector(selectModelsById);

  // Get Builder and Explorer assessments for status display
  const builderAssessments = useSelector(selectBuilderAssessments);
  const explorerAssessments = useSelector(selectExplorerAssessments);

  // Compute Builder/Explorer status per model
  const modelAssessmentStatus = useMemo(() => {
    const statusMap = {};

    // Map Builder assessments by model_uuid
    builderAssessments.forEach(assessment => {
      if (!assessment.model_uuid) return;
      if (!statusMap[assessment.model_uuid]) {
        statusMap[assessment.model_uuid] = { builder: null, explorer: [] };
      }
      statusMap[assessment.model_uuid].builder = {
        status: assessment.info?.status,
        assessmentUuid: assessment.info?.assessment_uuid,
        assessmentName: assessment.info?.assessment_name
      };
    });

    // Map Explorer assessments by model_uuid
    explorerAssessments.forEach(assessment => {
      if (!assessment.model_uuid) return;
      if (!statusMap[assessment.model_uuid]) {
        statusMap[assessment.model_uuid] = { builder: null, explorer: [] };
      }
      statusMap[assessment.model_uuid].explorer.push({
        status: assessment.info?.status,
        assessmentUuid: assessment.info?.assessment_uuid,
        assessmentName: assessment.info?.assessment_name
      });
    });

    return statusMap;
  }, [builderAssessments, explorerAssessments]);

  // Combine manual and cloud models into one array
  const manualModels = useMemo(() => {
    return Object.entries(modelsById).map(([model_uuid, model]) => ({
      ...model,
      // Normalize field names for both RTK and legacy formats
      model_uuid: model.model_uuid || model_uuid,
      modelName: model.model_name || model.modelName || 'Unnamed Model',
      modelType: model.model_type || model.modelType || 'Unknown',
      modelStage: model.model_stage || model.modelStage || 'Unknown',
      assessmentStatus: model.assessment_status || model.assessmentStatus || 'Not Started',
      origin: 'manual',
      source: 'Manual Entry'
    }));
  }, [modelsById]);

  const cloudModels = useSelector((state) => 
    (state.cloudModels.models || []).map(model => ({
      ...model,
      model_uuid: model.ModelCardName || model.modelName, // Use name as ID for cloud models
      modelName: model.ModelCardName || model.modelName,
      origin: 'imported',
      source: model.source || 'Unknown',
      created_at: model.CreationTime,
      assessmentStatus: 'Not Started',
      step: 'Not Started'
    }))
  );

  // Combined models array
  const allModels = useMemo(() => {
    const combined = [...manualModels, ...cloudModels];
    // Sort by created_at descending
    combined.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return combined;
  }, [manualModels, cloudModels]);

  // Use RTK loading state
  const isLoading = useSelector((state) => state.models.loading);
  const cloudModelsLoading = useSelector((state) => state.cloudModels.loading);

  // State
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [originFilter, setOriginFilter] = useState('all');
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [importMenuOpened, setImportMenuOpened] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Document upload state for model registration
  const [modalStep, setModalStep] = useState('form'); // 'form' or 'upload'
  const [newlyCreatedModelId, setNewlyCreatedModelId] = useState(null);
  const [newlyCreatedModelName, setNewlyCreatedModelName] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  // Model documents state (for model card indicator)
  const [modelDocuments, setModelDocuments] = useState({});

  // Explorer assessments modal state
  const [explorerModalOpen, setExplorerModalOpen] = useState(false);
  const [selectedExplorerModel, setSelectedExplorerModel] = useState(null);

  // Filtered models based on search and origin filter
  const filteredModels = useMemo(() => {
    return allModels.filter(model => {
      const matchesSearch = model.modelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           model.modelType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           model.source?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOrigin = originFilter === 'all' || model.origin === originFilter;
      return matchesSearch && matchesOrigin;
    });
  }, [allModels, searchTerm, originFilter]);

  // Paginated models
  const paginatedModels = useMemo(() => {
    const startIndex = (activePage - 1) * pageSize;
    return filteredModels.slice(startIndex, startIndex + pageSize);
  }, [filteredModels, activePage, pageSize]);

  const totalPages = Math.ceil(filteredModels.length / pageSize);

  const handleAddModel = () => {
    setEditingModel(null);
    setModalStep('form');
    setNewlyCreatedModelId(null);
    setNewlyCreatedModelName('');
    setUploadedDocuments([]);
    setOpenFormModal(true);
  };

  const handleCloseModal = () => {
    setOpenFormModal(false);
    setEditingModel(null);
    setModalStep('form');
    setNewlyCreatedModelId(null);
    setNewlyCreatedModelName('');
    setUploadedDocuments([]);
    setSavingModel(false);
  };

  const handleEditModel = (model) => {
    // Only allow editing manual models
    if (model.origin === 'manual') {
      setEditingModel(model);
      setOpenFormModal(true);
    } else {
      notifications.show({
        title: 'Cannot Edit',
        message: 'Imported models cannot be edited',
        color: 'yellow'
      });
    }
  };

  const handleDeleteClick = (model) => {
    if (model.origin === 'manual') {
      setModelToDelete(model);
      setDeleteModalOpen(true);
    } else {
      notifications.show({
        title: 'Cannot Delete',
        message: 'Imported models cannot be deleted',
        color: 'yellow'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) return;

    setDeleteModalOpen(false);
    setIsDeleting(true);

    try {
      // Use RTK thunk - token is retrieved from Redux state inside the thunk
      await dispatch(deleteModel(modelToDelete.model_uuid)).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Model deleted successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error deleting model:', error);
      notifications.show({
        title: 'Error',
        message: error || 'Failed to delete model',
        color: 'red'
      });
    } finally {
      setIsDeleting(false);
      setModelToDelete(null);
    }
  };

  const handleFormSubmit = async (formData, formType, modelId) => {
    let response;
    setSavingModel(true);         // start spinner
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai', 
          scope: 'read:models write:models delete:models' 
        }
      });

      const body = { ...formData };
      if (formType === 'update' && modelId) {
        body.modelId = modelId;
      }

      // Save or update the model
      if (formType === 'create') {
        response = await authFetch('/.netlify/functions/SaveModelCard', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      } else if (formType === 'update' && modelId) {
        response = await authFetch(`/.netlify/functions/SaveModelCard/${modelId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      }

      const responseBody = await response.json();
      if (responseBody.success) {
        const { data } = responseBody;

        notifications.show({
          title: 'Success',
          message: responseBody.message,
          color: 'green'
        });

        if (formType === 'create' && data?.model_uuid) {
          // For new models: transition to document upload step
          setNewlyCreatedModelId(data.model_uuid);
          setNewlyCreatedModelName(formData.modelName || data.model_name || 'Model');
          setModalStep('upload');
          setSavingModel(false);

          // Refresh models list in background
          dispatch(fetchAllModels());
        } else {
          // For updates: close modal and refresh
          setOpenFormModal(false);
          setEditingModel(null);
          setSavingModel(false);

          if (data) {
            setIsRefreshing(true);
            try {
              await dispatch(fetchAllModels()).unwrap();
            } finally {
              setIsRefreshing(false);
            }
          }
        }

      } else {
        // Server returned success = false
        notifications.show({
          title: 'Error',
          message: responseBody.message,
          color: 'red'
        });
        setOpenFormModal(false);
        setEditingModel(null);
        setSavingModel(false);
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: `An error occurred: ${error.message}`,
        color: 'red'
      });
      setOpenFormModal(false);
      setEditingModel(null);
      setSavingModel(false);
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" px="md">
        <Center h={400}>
          <Stack align="center">
            <Loader size="lg" />
            <Text c="dimmed">Loading models...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" px="md">
      <Stack spacing="xl">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconDatabase size={24} stroke={1.5} color="var(--mantine-color-dimmed)" />
            <Title order={2} fw={500}>Model Inventory</Title>
          </Group>

          <Group spacing="md">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleAddModel}
              color="blue"
              size="md"
              radius="md"
            >
              Add Model
            </Button>

            <Menu
              opened={importMenuOpened}
              onChange={setImportMenuOpened}
              shadow="md"
              width={200}
            >
              <Menu.Target>
                <Button
                  leftSection={<IconCloudUpload size={16} />}
                  variant="outline"
                  color="blue"
                  loading={isImporting}
                  size="md"
                  radius="md"
                >
                  Import
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item>
                  <ImportSageMakerModels setIsImporting={setIsImporting} />
                </Menu.Item>
                <Menu.Item>
                  <ImportBedrockModels setIsImporting={setIsImporting} />
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {(isLoading || cloudModelsLoading) && <Loader size="sm" />}
          </Group>
        </Group>

        {/* Origin Filter */}
        <Group gap="xs">
          <Text size="sm" c="dimmed" fw={500} w={50}>Origin:</Text>
          <Chip.Group value={originFilter} onChange={setOriginFilter}>
            <Group gap="xs">
              <Chip value="all" variant="light" size="sm" radius="md">
                All ({allModels.length})
              </Chip>
              <Chip value="manual" variant="light" size="sm" radius="md" color="blue">
                Manual ({manualModels.length})
              </Chip>
              <Chip value="imported" variant="light" size="sm" radius="md" color="cyan">
                Imported ({cloudModels.length})
              </Chip>
            </Group>
          </Chip.Group>
        </Group>

        <Divider />

        {/* Search */}
        <Group spacing="md">
          <TextInput
            placeholder="Search models..."
            leftSection={<IconSearch size={18} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
            size="md"
            radius="md"
          />
        </Group>

        {/* Models Table */}
        <Card shadow="sm" padding={0} radius="md" withBorder style={{ position: 'relative' }}>
          {/* Loading overlay for delete/refresh operations */}
          <Transition mounted={isDeleting || isRefreshing} transition="fade" duration={200}>
            {(styles) => (
              <Overlay
                style={styles}
                color="#fff"
                backgroundOpacity={0.85}
                blur={2}
                zIndex={10}
                radius="md"
              >
                <Center h="100%">
                  <Stack align="center" gap="xs">
                    <Loader size="md" color="blue" />
                    <Text size="sm" c="dimmed" fw={500}>
                      {isDeleting ? 'Deleting model...' : 'Updating...'}
                    </Text>
                  </Stack>
                </Center>
              </Overlay>
            )}
          </Transition>

          <Table highlightOnHover verticalSpacing="md" fontSize="sm">
            <thead style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <tr>
                <th style={{ padding: '16px 20px', fontWeight: 600, textAlign: 'left', minWidth: '200px' }}>Model Name</th>
                <th style={{ width: 40, padding: '16px 8px', fontWeight: 600, textAlign: 'center' }}>
                  <Tooltip label="Model Card Document" withArrow>
                    <span><IconFileDescription size={16} color="gray" /></span>
                  </Tooltip>
                </th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Type</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Stage</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Builder</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'center', width: 80 }}>Explorer</th>
                <th style={{ width: 100, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Created By</th>
                <th style={{ width: 90, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedModels.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <Center py={60}>
                      <Stack align="center" spacing="md">
                        <Text size="lg" weight={500} c="dimmed">
                          No models found
                        </Text>
                        <Text size="sm" c="dimmed">
                          {searchTerm ? `No results for "${searchTerm}"` : 'No models available'}
                        </Text>
                      </Stack>
                    </Center>
                  </td>
                </tr>
              ) : (
                paginatedModels.map((model) => {
                  const assessmentStatus = modelAssessmentStatus[model.model_uuid] || { builder: null, explorer: [] };
                  const builderStatus = assessmentStatus.builder;
                  const explorerList = assessmentStatus.explorer || [];
                  const modelDocs = modelDocuments[model.model_uuid] || [];

                  return (
                  <tr key={model.model_uuid}>
                    <td style={{ padding: '16px 20px', textAlign: 'left' }}>
                      <Text size="sm" weight={600}>
                        {model.modelName}
                      </Text>
                    </td>
                    {/* Model Card Document Indicator */}
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {modelDocs.length > 0 ? (
                        <Tooltip
                          label={
                            <Stack gap={2}>
                              {modelDocs.map((doc, i) => (
                                <Text key={i} size="xs">{doc.file_name}</Text>
                              ))}
                            </Stack>
                          }
                          withArrow
                        >
                          <ThemeIcon color="green" size="sm" radius="xl" variant="light">
                            <IconCheck size={12} />
                          </ThemeIcon>
                        </Tooltip>
                      ) : (
                        <Text size="sm" c="dimmed">—</Text>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Text size="sm" c="dimmed">{model.modelType || '—'}</Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Text size="sm" c="dimmed">{model.modelStage || '—'}</Text>
                    </td>
                    {/* Builder Column */}
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      {!builderStatus ? (
                        <Text size="sm" c="dimmed">—</Text>
                      ) : (
                        <Tooltip label={builderStatus.assessmentName || 'Builder Assessment'} withArrow>
                          <Badge
                            variant="light"
                            color={builderStatus.status === 'Completed' ? 'green' : builderStatus.status === 'On-Going' ? 'yellow' : 'gray'}
                            size="sm"
                            leftSection={builderStatus.status === 'Completed' ? <IconCheck size={12} /> : builderStatus.status === 'On-Going' ? <IconClock size={12} /> : null}
                          >
                            {builderStatus.status === 'Completed' ? '✓' : builderStatus.status === 'On-Going' ? '◐' : '—'}
                          </Badge>
                        </Tooltip>
                      )}
                    </td>
                    {/* Explorer Assessments Column - Count Badge */}
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      {explorerList.length === 0 ? (
                        <Text size="sm" c="dimmed">—</Text>
                      ) : (
                        <Tooltip
                          label={
                            <Stack gap={4}>
                              <Text size="xs" fw={600}>Explorer Assessments</Text>
                              {explorerList.map((exp, i) => (
                                <Text key={i} size="xs">
                                  • {exp.assessmentName} {exp.status === 'Completed' ? '✓' : '◐'}
                                </Text>
                              ))}
                            </Stack>
                          }
                          withArrow
                          multiline
                          w={250}
                        >
                          <Badge
                            variant="light"
                            color="blue"
                            size="md"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedExplorerModel({ name: model.modelName, explorerList });
                              setExplorerModalOpen(true);
                            }}
                          >
                            {explorerList.length}
                          </Badge>
                        </Tooltip>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <Center>
                        <Tooltip label={model.createdByName || 'Unknown User'} withArrow>
                          <Avatar
                            src={model.avatar}
                            size={28}
                            radius="xl"
                            color="blue"
                          >
                            <IconUser size={16} />
                          </Avatar>
                        </Tooltip>
                      </Center>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <Group spacing="xs" position="center" align="center" wrap="nowrap">
                      <Tooltip
                        label={model.origin === 'manual' ? 'Edit Model' : 'Cannot edit imported models'}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="lg"
                          radius="md"
                          onClick={() => handleEditModel(model)}
                          disabled={model.origin !== 'manual'}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={model.origin === 'manual' ? 'Delete Model' : 'Cannot delete imported models'}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="lg"
                          radius="md"
                          onClick={() => handleDeleteClick(model)}
                          disabled={model.origin !== 'manual'}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Group position="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Pagination
                value={activePage}
                onChange={setActivePage}
                total={totalPages}
                size="sm"
                radius="md"
              />
            </Group>
          )}
        </Card>
      </Stack>

      {/* Model Form Modal */}
      <Modal
        opened={openFormModal}
        onClose={handleCloseModal}
        title={
          modalStep === 'upload'
            ? `Upload Documentation - ${newlyCreatedModelName}`
            : editingModel ? 'Update Model Card' : 'Add Model Card'
        }
        size="lg"
        centered
        radius="md"
      >
        {/* Stepper indicator for new model creation */}
        {!editingModel && (
          <Stepper active={modalStep === 'form' ? 0 : 1} size="xs" mb="lg">
            <Stepper.Step label="Model Details" icon={<IconDatabase size={18} />} />
            <Stepper.Step label="Documentation" icon={<IconFileDescription size={18} />} />
          </Stepper>
        )}

        {modalStep === 'form' ? (
          <>
            <ModelCardForm
              onSubmit={handleFormSubmit}
              modelId={editingModel?.model_uuid}
              initialData={editingModel || {}}
            />
            <Group position="right" mt="md">
              <Button
                variant="subtle"
                onClick={handleCloseModal}
                radius="md"
              >
                Cancel
              </Button>
              <Button
                color="teal"
                onClick={() => {
                  const formEl = document.querySelector('form');
                  if (formEl && !savingModel) {
                    formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                  }
                }}
                loading={savingModel}
                radius="md"
                rightSection={!editingModel && <IconArrowRight size={16} />}
              >
                {editingModel ? 'Update' : 'Save & Continue'}
              </Button>
            </Group>
          </>
        ) : (
          /* Document Upload Step */
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Upload model documentation to enable AI-powered risk discovery.
              You can also do this later from the assessment workflow.
            </Text>

            <DocumentUpload
              modelId={newlyCreatedModelId}
              documentType="model_card"
              label="Model Card / Documentation"
              description="Upload model card, system documentation, or technical specifications"
              optional={true}
              showImportButton={true}
              existingDocuments={uploadedDocuments}
              onUploadComplete={(doc) => {
                setUploadedDocuments(prev => [...prev, doc]);
              }}
              onDocumentDeleted={(docId) => {
                setUploadedDocuments(prev => prev.filter(d => d.id !== docId));
              }}
            />

            <Divider />

            <Group position="apart">
              <Button
                variant="subtle"
                onClick={handleCloseModal}
                radius="md"
              >
                Skip for now
              </Button>
              <Button
                color="teal"
                onClick={handleCloseModal}
                radius="md"
              >
                {uploadedDocuments.length > 0 ? 'Done' : 'Finish'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
        centered
        radius="md"
      >
        <Text mb="md">
          Are you sure you want to delete the model "{modelToDelete?.modelName}"?
        </Text>
        <Group position="right">
          <Button
            variant="subtle"
            onClick={() => setDeleteModalOpen(false)}
            radius="md"
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteConfirm}
            radius="md"
          >
            Confirm
          </Button>
        </Group>
      </Modal>

      {/* Explorer Assessments Modal */}
      <Modal
        opened={explorerModalOpen}
        onClose={() => {
          setExplorerModalOpen(false);
          setSelectedExplorerModel(null);
        }}
        title={`Explorer Assessments - ${selectedExplorerModel?.name || ''}`}
        centered
        radius="md"
        size="md"
      >
        {selectedExplorerModel?.explorerList?.length > 0 ? (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Assessment Name</th>
                <th style={{ width: 80, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {selectedExplorerModel.explorerList.map((exp, i) => (
                <tr key={i}>
                  <td>
                    <Text size="sm">{exp.assessmentName || 'Unnamed'}</Text>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Badge
                      variant="light"
                      color={exp.status === 'Completed' ? 'green' : exp.status === 'On-Going' ? 'yellow' : 'gray'}
                      size="sm"
                    >
                      {exp.status === 'Completed' ? '✓' : '◐'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Text c="dimmed" ta="center" py="md">No explorer assessments found.</Text>
        )}
        <Group justify="flex-end" mt="md">
          <Button
            variant="light"
            onClick={() => {
              setExplorerModalOpen(false);
              setSelectedExplorerModel(null);
            }}
            radius="md"
          >
            Close
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}

export default ModelInventory;
